import React, { useEffect, useRef, useState } from 'react'

export function drawChart() {}

const MESES_FULL  = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
const MESES_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

// One color per year — cyan first (matches screenshot exactly), then purple, teal, amber
const COLORS = [
    { bar:'#38bdf8', cap:'#7dd3fc', glow:'#0ea5e9', dark:'#0c4a6e', label:'#bae6fd' },
    { bar:'#a78bfa', cap:'#c4b5fd', glow:'#7c3aed', dark:'#2e1065', label:'#ddd6fe' },
    { bar:'#34d399', cap:'#6ee7b7', glow:'#059669', dark:'#064e3b', label:'#a7f3d0' },
    { bar:'#fb923c', cap:'#fdba74', glow:'#ea580c', dark:'#431407', label:'#fed7aa' },
]

function shortMoney(v) {
    if (!v || isNaN(v)) return '$0'
    if (v >= 1e6) return '$' + (v/1e6).toFixed(1) + 'M'
    if (v >= 1e3) return '$' + (v/1e3).toFixed(0) + 'K'
    return '$' + Math.round(v)
}

function fullMoney(v) {
    if (!v || isNaN(v)) return '$0.00'
    return '$' + Number(v).toLocaleString('es-MX', {minimumFractionDigits:2, maximumFractionDigits:2})
}

export default function Chart({ ingresosPerMonthYear }) {
    const wrapRef  = useRef(null)
    const [W, setW] = useState(860)
    const [prog, setProg] = useState(0)
    const [tip, setTip]   = useState(null)

    // data
    const raw   = (ingresosPerMonthYear || []).map(d => ({...d, v: Math.max(0, +d.ingresos||0)}))
    const years = [...new Set(raw.map(d=>d.year))].sort()
    const maxV  = Math.max(...raw.map(d=>d.v), 1)

    // responsive
    useEffect(() => {
        if (!wrapRef.current) return
        let raf
        const ro = new ResizeObserver(es => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(() => {
                const w = es[0]?.contentRect?.width
                if (w > 0) setW(w)
            })
        })
        ro.observe(wrapRef.current)
        return () => { ro.disconnect(); cancelAnimationFrame(raf) }
    }, [])

    // animate on data/width change
    useEffect(() => {
        setProg(0)
        let start, raf
        const run = ts => {
            if (!start) start = ts
            const t = Math.min((ts-start)/700, 1)
            setProg(1 - Math.pow(1-t, 3))   // cubic ease-out
            if (t < 1) raf = requestAnimationFrame(run)
        }
        raf = requestAnimationFrame(run)
        return () => cancelAnimationFrame(raf)
    }, [raw.length, W])

    if (!raw.length) return null

    // layout
    const ML=68, MR=24, MT=32, MB=62
    const svgH  = Math.max(300, Math.min(420, W*0.42))
    const GW    = W - ML - MR
    const GH    = svgH - MT - MB

    const N     = raw.length
    const slot  = GW / N
    const bw    = Math.min(44, Math.max(8, slot * 0.6))
    const bx    = i => i*slot + slot/2 - bw/2

    const sy    = v => GH - (v/maxV)*GH*0.88   // top Y of bar
    const bh    = v => GH - sy(v)               // bar height

    // grid
    const ticks = [0,.25,.5,.75,1].map(f => maxV*f)

    // year x-centers for label
    const yCenters = years.map(yr => {
        const idxs = raw.map((d,i)=>d.year===yr?i:-1).filter(i=>i>=0)
        return { yr, cx: idxs.reduce((a,b)=>a+b,0)/idxs.length*slot + slot/2 }
    })

    // year separator lines
    const ySeps = years.slice(1).map(yr => raw.findIndex(d=>d.year===yr)*slot)

    return (
        <div ref={wrapRef} style={{width:'100%', position:'relative'}}>

            {/* Dark chart container */}
            <div style={{
                background: 'linear-gradient(160deg,#0f1e35 0%,#0a1628 60%,#060e1a 100%)',
                borderRadius: 16,
                padding: '24px 20px 16px',
                border: '1px solid rgba(56,189,248,0.12)',
                boxShadow: '0 0 40px rgba(14,165,233,0.06), inset 0 1px 0 rgba(255,255,255,0.04)',
                position: 'relative',
                overflow: 'hidden',
            }}>

                {/* subtle grid texture bg */}
                <div style={{
                    position:'absolute', inset:0, opacity:0.03,
                    backgroundImage:'linear-gradient(rgba(56,189,248,1) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,1) 1px,transparent 1px)',
                    backgroundSize:'40px 40px',
                    pointerEvents:'none',
                }} />

                {/* Legend */}
                <div style={{display:'flex', gap:20, marginBottom:20, flexWrap:'wrap', position:'relative'}}>
                    {years.map((yr,i) => {
                        const c = COLORS[i%COLORS.length]
                        return (
                            <div key={yr} style={{display:'flex', alignItems:'center', gap:8, fontSize:'0.78rem', fontWeight:700, color:c.label, fontFamily:'monospace', letterSpacing:'1px'}}>
                                <span style={{
                                    width:32, height:10, borderRadius:3, display:'inline-block',
                                    background:`linear-gradient(90deg,${c.cap},${c.bar})`,
                                    boxShadow:`0 0 12px ${c.bar}99`,
                                }}/>
                                {yr}
                            </div>
                        )
                    })}
                </div>

                <svg width={W} height={svgH} style={{display:'block', overflow:'visible', position:'relative'}}>
                    <defs>
                        {COLORS.map((c,i) => (
                            <React.Fragment key={i}>
                                {/* Bar gradient top→bottom */}
                                <linearGradient id={`bg${i}`} x1="0" x2="0" y1="0" y2="1">
                                    <stop offset="0%"   stopColor={c.cap} stopOpacity="1"/>
                                    <stop offset="40%"  stopColor={c.bar} stopOpacity="1"/>
                                    <stop offset="100%" stopColor={c.dark} stopOpacity="0.9"/>
                                </linearGradient>
                                {/* Glow blur */}
                                <filter id={`gf${i}`} x="-60%" y="-20%" width="220%" height="150%">
                                    <feGaussianBlur stdDeviation="8" result="b"/>
                                    <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                                </filter>
                            </React.Fragment>
                        ))}
                    </defs>

                    <g transform={`translate(${ML},${MT})`}>

                        {/* Grid */}
                        {ticks.map((v,i) => (
                            <g key={i}>
                                <line x1={0} x2={GW} y1={sy(v)} y2={sy(v)}
                                    stroke={i===0?'rgba(56,189,248,0.2)':'rgba(56,189,248,0.06)'}
                                    strokeWidth={1} strokeDasharray={i===0?'none':'4 4'}/>
                                <text x={-10} y={sy(v)} textAnchor="end" dominantBaseline="middle"
                                    fill="#475569" fontSize="11" fontFamily="'JetBrains Mono','Courier New',monospace" fontWeight="500">
                                    {shortMoney(v)}
                                </text>
                            </g>
                        ))}

                        {/* Year separators */}
                        {ySeps.map((sx,i) => (
                            <line key={i} x1={sx} x2={sx} y1={0} y2={GH}
                                stroke="rgba(56,189,248,0.15)" strokeWidth={1} strokeDasharray="3 4"/>
                        ))}

                        {/* Bars */}
                        {raw.map((d,i) => {
                            const yi = years.indexOf(d.year)
                            const c  = COLORS[yi%COLORS.length]
                            const x  = bx(i)
                            const fullBH = bh(d.v)
                            const animBH = fullBH * prog
                            const animY  = GH - animBH
                            if (d.v <= 0) return null

                            const mesIdx = MESES_FULL.indexOf(d.mes)
                            const mShort = mesIdx>=0 ? MESES_SHORT[mesIdx] : String(d.mes||'').slice(0,3)

                            return (
                                <g key={i} style={{cursor:'pointer'}}
                                    onMouseEnter={()=>setTip({x:x+bw/2, y:animY, d, c, mShort})}
                                    onMouseLeave={()=>setTip(null)}>

                                    {/* Glow halo */}
                                    <rect x={x-4} y={animY-2} width={bw+8} height={animBH+4}
                                        rx={6} fill={c.bar} opacity={0.18}
                                        filter={`url(#gf${yi})`}/>

                                    {/* Bar */}
                                    <rect x={x} y={animY} width={bw} height={animBH}
                                        rx={Math.min(6,bw*0.25)}
                                        fill={`url(#bg${yi})`}/>

                                    {/* Neon top cap line */}
                                    <rect x={x} y={animY} width={bw} height={Math.min(3,animBH)}
                                        rx={2} fill={c.cap} opacity={1}/>

                                    {/* Inner left shine */}
                                    <rect x={x+2} y={animY+3} width={Math.max(2,bw*0.2)} height={Math.max(0,animBH-6)}
                                        rx={2} fill="white" opacity={0.12}/>

                                    {/* Value label above bar */}
                                    {prog > 0.9 && animBH > 16 && (
                                        <text x={x+bw/2} y={animY-8} textAnchor="middle"
                                            fill={c.label} fontSize={bw<20?8:10} fontWeight="700"
                                            fontFamily="'JetBrains Mono','Courier New',monospace">
                                            {shortMoney(d.v)}
                                        </text>
                                    )}

                                    {/* Month label */}
                                    <text x={x+bw/2} y={GH+14}
                                        textAnchor="end" dominantBaseline="middle"
                                        fill="rgba(148,163,184,0.5)"
                                        fontSize={N>30?7:9} fontFamily="monospace"
                                        transform={`rotate(-50,${x+bw/2},${GH+14})`}>
                                        {mShort}
                                    </text>
                                </g>
                            )
                        })}

                        {/* Year labels */}
                        {yCenters.map(({yr,cx},i)=>{
                            const c = COLORS[i%COLORS.length]
                            return (
                                <text key={yr} x={cx} y={GH+52}
                                    textAnchor="middle" fill={c.bar}
                                    fontSize="13" fontWeight="800"
                                    fontFamily="'JetBrains Mono','Courier New',monospace"
                                    filter={`url(#gf${i})`}>
                                    {yr}
                                </text>
                            )
                        })}

                        {/* Tooltip (SVG) */}
                        {tip && (() => {
                            const tx = Math.min(Math.max(tip.x, 80), GW-80)
                            const ty = Math.max(tip.y-10, 10)
                            return (
                                <g>
                                    {/* dark pill */}
                                    <rect x={tx-75} y={ty-58} width={155} height={50}
                                        rx={10} fill="#060e1a" fillOpacity={0.98}
                                        stroke={tip.c.bar} strokeWidth={1} strokeOpacity={0.7}/>
                                    {/* glow border */}
                                    <rect x={tx-75} y={ty-58} width={155} height={50}
                                        rx={10} fill="none"
                                        stroke={tip.c.bar} strokeWidth={6} strokeOpacity={0.1}/>
                                    <text x={tx} y={ty-38} textAnchor="middle"
                                        fill="#64748b" fontSize="9" fontFamily="monospace" letterSpacing="1.5">
                                        {`${tip.mShort} · ${tip.d.year}`.toUpperCase()}
                                    </text>
                                    <text x={tx} y={ty-16} textAnchor="middle"
                                        fill={tip.c.label} fontSize="15" fontWeight="800"
                                        fontFamily="'JetBrains Mono','Courier New',monospace">
                                        {fullMoney(tip.d.v)}
                                    </text>
                                </g>
                            )
                        })()}

                    </g>
                </svg>
            </div>
        </div>
    )
}
